<template>
  <pl-dialog title="Connect LMS" :size="'lg'" :disableOutsideClick="true" @close="closeModal">
    <div class="mt-8 mb-6 px-7 flex font-semibold items-center">
      <img src="/static/google_class_round.png" class="w-10 h-10" />
      <span class="text-lg ml-3">Google Classroom</span>
    </div>
    <div v-if="!students_tab" class="px-7">
      <h6 class="mt-3 mb-1 text-sm text-gray-500">
        Which Google Classroom class do you want to connect to {{ getCourseDetail.title }} in Parlay?
      </h6>
    </div>
    <div class="px-7 flex-grow overflow-y-auto">
      <template v-if="loading">
        <div class="w-full h-full my-56 flex justify-center">
          <pl-spinner />
        </div>
      </template>
      <template v-else>
        <div v-if="!students_tab" class="select-classroom">
          <template v-if="!courses.length">
            <span> Your Google Classroom account has no courses </span>
          </template>
          <template v-else>
            <pl-select
              :options="courses"
              label="name"
              :selected="selected_course"
              :arrow="true"
              :placeholder="'Select a Classroom'"
              @change="getCourseUsers($event)"
              class="mb-4"
            >
            </pl-select>
          </template>
        </div>
        <div v-else>
          <div v-if="students && students.length">
            <div class="my-3 text-base">Select Students</div>
            <div class="grid grid-cols-2 h-full gap-x-3">
              <div class="flex flex-col">
                <p class="text-sm">Available</p>
                <div class="w-full h-96 p-3 border border-gray-100 rounded overflow-auto">
                  <div class="mb-2 bg-white border rounded border-gray-100">
                    <input
                      v-model="search_string_available"
                      class="p-2 text-sm placeholder-gray-200 w-full text-gray-500 bg-input"
                      type="text"
                      placeholder="Search..."
                    />
                  </div>
                  <div v-if="sorted_available_list && sorted_available_list.length" class="flex flex-nowrap">
                    <pl-checkbox
                      @change="handleSelectAll($event)"
                      v-model="is_all_selected"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500">
                      {{ is_all_selected ? 'De-select All' : 'Select All' }}
                    </span>
                  </div>
                  <div v-for="student in sorted_available_list" :key="student.userId" class="flex flex-nowrap my-2">
                    <pl-checkbox
                      @change="handleSelectStudent"
                      v-model="student.selected"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500">
                      {{ student.profile.name.fullName }}
                    </span>
                  </div>
                </div>
                <button @click="handleInviteStudents" class="px-8 py-2 my-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50" :disabled="!students_selected_for_invite_ids.length">
                  Invite
                </button>
              </div>
              <div class="flex flex-col">
                <p class="text-sm">Invited</p>
                <div class="w-full h-96 p-3 border border-gray-100 rounded overflow-auto">
                  <div class="mb-2 bg-white border rounded border-gray-100">
                    <input
                      v-model="search_string_invited"
                      class="p-2 text-sm placeholder-gray-200 w-full text-gray-500 bg-input"
                      type="text"
                      placeholder="Search..."
                    />
                  </div>
                  <div class="flex flex-nowrap" v-if="sorted_invited_list.length">
                    <pl-checkbox
                      @change="handleSelectAllUninvite($event)"
                      v-model="is_all_selected_invited"
                      checkBoxClass="checkmark-red"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500">
                      {{ is_all_selected_invited ? 'De-select All' : 'Select All' }}
                    </span>
                  </div>
                  <div v-for="student in sorted_invited_list" :key="student.userId" class="flex flex-nowrap my-2">
                    <pl-checkbox
                      @change="handleSelectStudentUninvite"
                      v-model="student.selected"
                      checkBoxClass="checkmark-red"
                      :disabled="remove_student_mode"
                    />
                    <span class="text-gray-500"> {{ student.name }} </span>
                  </div>
                </div>
                <button
                  @click="removeStudents"
                  class="px-8 py-2 my-2 bg-red-400 rounded cursor-pointer text-white font-semibold disabled:opacity-50"
                  :disabled="!students_selected_for_available_ids.length"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
          <div v-else class="font-semibold">
            There are no students in the course you are trying to connect. Want
            to connect anyway? Click finish.
          </div>
        </div>
      </template>
    </div>
    <div v-if="!students_tab" class="absolute bottom-0 w-full flex items-center justify-end py-4 px-7">
      <div class="flex">
        <button @click="closeModal" class="px-8 py-2 hover:bg-gray-100 rounded cursor-pointer text-gray-500 font-semibold mr-2.5">
          Cancel
        </button>
        <button v-if="!googleClassroom_connected" :disabled="!selected_course || import_students_loading" @click="handleInviteStudents" class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50">
          Done
        </button>
      </div>
    </div>
  </pl-dialog>
</template>

<script>
  import { mapGetters } from 'vuex';
  import googleClassroomService from 'services/googleClassroom/googleClassroomServices';
  import { EventBus } from 'utils/event-bus';
  import getBillingInformation from 'services/account/getBillingInformation';

  export default {
    components: {},
    watch: {
      students_tab() {
        this.handleSelectAll(true);
      },
    },
    props: {
      courses: {
        type: Array,
        default() {
          return [];
        },
      },
    },
    data() {
      return {
        google_classroom_course_id: '',
        google_classroom_course_name: '',
        students_tab: false,
        selected_course: null,
        is_all_selected: true,
        is_all_selected_invited: false,
        students: [],
        teachers: [],
        students_to_invite: [],
        invited_list: [],
        remove_student_mode: false,
        loading: false,
        import_students_loading: false,
        search_string_available: '',
        search_string_invited: '',
        studentsListAvailable: [],
        studentsListInvited: [],
        isDone: false,
        membership: null,
      };
    },
    computed: {
      ...mapGetters(['getCourseDetail', 'getProfile']),
      googleClassroom_connected() {
        if (this.getCourseDetail.google_classroom_integration.course_id) {
          return true;
        }
        return false;
      },
      available_students_to_invite() {
        if (this.students && this.students.length) {
          return this.students.filter(
            (s) =>
              !this.invited_list
                .map((i) => i.email)
                .includes(s.profile.emailAddress)
          );
        }
        return [];
      },
      sorted_available_list() {
        const results = this.available_students_to_invite.filter((x) =>
          x.profile.name.fullName
            .toLowerCase()
            .includes(this.search_string_available.toLowerCase())
        );
        return results.sort((a, b) => {
          return a.profile.name.fullName.localeCompare(b.profile.name.fullName);
        });
      },
      sorted_invited_list() {
        const results = this.invited_list.filter((x) =>
          x.name
            .toLowerCase()
            .includes(this.search_string_invited.toLowerCase())
        );
        return results.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      },
      students_selected_for_available_ids() {
        return this.invited_list
          .filter((student) => student.selected === true)
          .map((s) => s.id);
      },
      students_selected_for_invite_ids() {
        return this.available_students_to_invite
          .filter((student) => student.selected === true)
          .map((s) => s.userId);
      },
    },
    methods: {
      getCourseUsers(gc_course) {
        this.getTeachersOfCourse(gc_course);
        this.getStudentsOfCourse(gc_course);
      },
      getTeachersOfCourse(gc_course) {
        this.loading = true;
        this.selected_course = gc_course;
        googleClassroomService
          .getGoogleClassroomCourseTeachers(
            this.getCourseDetail.id,
            gc_course.id
          )
          .then(({ data }) => {
            this.teachers = data.teachers;
          })
          .catch((err) => console.error(err))
          .finally(() => {
            this.loading = false;
          });
      },
      getStudentsOfCourse(gc_course) {
        this.loading = true;
        this.selected_course = gc_course;
        googleClassroomService
          .getGoogleClassroomCourseStudents(
            this.getCourseDetail.id,
            gc_course.id
          )
          .then(({ data }) => {
            this.students = data.students.map((s) => {
              return { ...s, selected: true };
            });
            this.google_classroom_course_id = gc_course.id;
            this.google_classroom_course_name = gc_course.name;
            if (this.students && this.students.length) {
              this.invited_list = this.getCourseDetail.users.filter((u) => {
                return (
                  this.students
                    .map((s) => s.profile.emailAddress)
                    .includes(u.email) &&
                  u.subscription.google_classroom_integration &&
                  u.subscription.google_classroom_integration.user_id
                );
              });
              this.invited_list = this.invited_list.map((s) => {
                return { ...s, selected: false };
              });
            }
          })
          .catch((err) => {
            const message = err.response.data.error;
            if (message) EventBus.$emit('toast-msg', message);
            console.error(err);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      handleSelectAll(status) {
        this.available_students_to_invite.map((s) => {
          s.selected = status;
        });
      },
      handleSelectAllUninvite(status) {
        this.invited_list.map((s) => {
          s.selected = status;
        });
      },
      handleSelectStudent() {
        this.is_all_selected =
          this.students_selected_for_invite_ids.length ===
          this.available_students_to_invite.length;
      },
      handleSelectStudentUninvite() {
        this.is_all_selected_invited =
          this.students_selected_for_available_ids.length ===
          this.invited_list.length;
      },
      resetSelectedAll() {
        this.is_all_selected = false;
        this.is_all_selected_invited = false;
      },
      handleInviteStudents: async function() {
        try {
          this.import_students_loading = true;

          const MAX_FREE_COURSE_USERS = 30;
          const users_in_course = this.getCourseDetail.users.filter((u) => {
            return ['student'].includes(u.subscription.role);
          });
          const users_in_course_count = users_in_course.length;

          const students = [];
          const courseID = this.$route.params.courseID;
          const students_to_invite = this.available_students_to_invite.filter((s) => this.students_selected_for_invite_ids.includes(s.userId));

          students_to_invite.forEach((s) => {
            students.push({ name: s.profile.name.fullName, email: s.profile.emailAddress, role: 'student', google_classroom_user_id: s.userId });
          });

          if (this.membership === null && (users_in_course_count + students_to_invite.length) > MAX_FREE_COURSE_USERS) {
            const spots_left = MAX_FREE_COURSE_USERS - users_in_course_count;
            EventBus.$emit('createToast', {
              type: 'Error',
              message: `You can only invite ${spots_left} more student${spots_left === 1 ? '' : 's'} (you have ${users_in_course_count} of ${MAX_FREE_COURSE_USERS}).`,
              time: 5000
            });
            this.import_students_loading = false;
            return;
          }
          
          const payload = {
            course_id: courseID,
            google_classroom_course_id: this.google_classroom_course_id,
            google_classroom_course_name: this.google_classroom_course_name,
            classroom_users: students,
          };

          await this.$store.dispatch('syncGoogleClassroomCourse', payload);
          await this.$store.dispatch('fetchCourseDetails', courseID);

          this.import_students_loading = false;

          EventBus.$emit('createToast', { type: 'Success', message: 'Course invite list changed', time: 5000 });

          this.getStudentsOfCourse(this.selected_course);

          const { inviteModalOpen, roundtableID, liveDiscussionID } = this.$route.query;

          if (inviteModalOpen && (roundtableID || liveDiscussionID)) {
            this.$router.push({ 
              name: roundtableID  ? 'roundtable.discussion' : 'live-discussion.discussion',
              params: {
                courseID: this.$route.params.courseID,
                ...(roundtableID ? { roundtableID } : { liveDiscussionID })
              },
              query: { inviteModalOpen: true }
            });
          } 
          
          this.$emit('close');
        } catch (error) {
          console.error(error);
          this.import_students_loading = false;
          EventBus.$emit('createToast', { type: 'Error', message: 'Something went wrong, please try again.', time: 5000 });
        }
      },
      checkIntegrationTeachersInParlay() {
        const parlay_teachers_emails = this.getCourseDetail.users
          .filter((u) => ['co-teacher', 'author'].includes(u.subscription.role))
          .map((x) => x.email);
        const integration_teachers_emails = this.teachers.map(
          (t) => t.profile.emailAddress
        );
        const missing_teachers = [];
        integration_teachers_emails.forEach((email) => {
          if (!parlay_teachers_emails.includes(email))
            missing_teachers.push(email);
        });
        return missing_teachers;
      },
      removeStudents() {
        const course_id = this.$route.params.courseID;
        this.$store
          .dispatch('unsyncStudentFromCourse', {
            course_id,
            student_ids: this.students_selected_for_available_ids,
          })
          .then(async () => {
            this.import_students_loading = false;
            this.remove_student_mode = false;
            await this.getStudentsOfCourse(this.selected_course);
            const toast = {
              type: 'Success',
              message: 'Course invite list changed',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          })
          .catch(() => {
            this.import_students_loading = false;
            const toast = {
              type: 'Error',
              message: 'Something went wrong, please try again.',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          });
      },
      updateRoster() {
        if (this.getCourseDetail.google_classroom_integration.course_id) {
          this.selected_course = {
            id: this.getCourseDetail.google_classroom_integration.course_id,
            name: this.getCourseDetail.google_classroom_integration.course_name,
          };
          if (!this.students.length) {
            this.getStudentsOfCourse(this.selected_course);
          }
        }
      },
      closeModal() {
        if (
          this.$route.query.inviteModalOpen &&
          this.$route.query.roundtableID
        ) {
          this.$router.push({
            name: 'roundtable.index',
            params: {
              courseID: this.$route.params.courseID,
              roundtableID: this.$route.query.roundtableID,
            },
            query: {
              inviteModalOpen: true,
            },
          });
        } else if (
          this.$route.query.inviteModalOpen &&
          this.$route.query.liveDiscussionID
        ) {
          this.$router.push({
            name: 'live-discussion.index',
            params: {
              courseID: this.$route.params.courseID,
              liveDiscussionID: this.$route.query.liveDiscussionID,
            },
            query: {
              inviteModalOpen: true,
            },
          });
        }
        this.$emit('close');
      },
    },
    mounted: async function() {
      const { data } = await getBillingInformation();
      this.membership = data.memberships;
      this.students_tab = this.getCourseDetail.google_classroom_integration && this.getCourseDetail.google_classroom_integration.course_id != null;
      this.updateRoster();
      
    },
  };
</script>
<style scoped lang="stylus">
  .nav-btn
    cursor: pointer
    color: #d8d8d8
    &.selected
      color: #72ccd7
  ::-webkit-scrollbar
    display: block
    width: 10px
    -webkit-appearance: none
</style>
<style lang="stylus">
  .select-classroom
    .custom-select
      padding: 0 !important
      .items
        max-height: calc(80vh - 300px) !important
      .selected
        padding: 10px !important
        border: 1px solid rgba(219, 223, 234, 1)
        border-radius: 5px
</style>



// WEBPACK FOOTER //
// googleClassroomModal.vue?77611fe5